import React from 'react';
import {
  Flex,
  HStack,
  Button,
  IconButton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  showPageNumbers = true,
  showInfo = true,
  size = 'md',
  variant = 'outline',
  colorScheme = 'blue',
  siblingCount = 1,
  boundaryCount = 1,
  ...props
}) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  if (totalPages <= 1) return null;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  // Generate page numbers to show
  const generatePageNumbers = () => {
    const totalNumbers = siblingCount * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - siblingCount);
      const endPage = Math.min(totalPages - 1, currentPage + siblingCount);

      let pages = [];

      if (currentPage <= siblingCount + 2) {
        // Near the start
        for (let i = 1; i <= totalNumbers - 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - siblingCount - 1) {
        // Near the end
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - totalNumbers + 2; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        pages.push('...');
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }

      return pages;
    }

    // Show all pages if total pages is small
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const pageNumbers = generatePageNumbers();

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      align="center"
      gap={4}
      mt={4}
      p={3}
      borderTop="1px solid"
      borderColor={borderColor}
      {...props}
    >
      {showInfo && (
        <Text fontSize="sm" color={textColor}>
          Page {currentPage} of {totalPages}
        </Text>
      )}

      <HStack spacing={1}>
        <IconButton
          aria-label="Previous Page"
          icon={<ChevronLeftIcon />}
          isDisabled={currentPage <= 1}
          onClick={() => handlePageChange(currentPage - 1)}
          size={size}
          variant={variant}
          colorScheme={colorScheme}
          borderRadius="md"
        />

        {showPageNumbers &&
          pageNumbers.map((pageNum, index) => {
            if (pageNum === '...') {
              return (
                <Button
                  key={`ellipsis-${index}`}
                  size={size}
                  variant="ghost"
                  isDisabled
                  _hover={{ bg: 'transparent' }}
                  cursor="default"
                  color={textColor}
                >
                  ...
                </Button>
              );
            }

            return (
              <Button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                colorScheme={currentPage === pageNum ? colorScheme : 'gray'}
                variant={currentPage === pageNum ? 'solid' : 'ghost'}
                size={size}
                borderRadius="md"
                minW="8"
              >
                {pageNum}
              </Button>
            );
          })}

        <IconButton
          aria-label="Next Page"
          icon={<ChevronRightIcon />}
          isDisabled={currentPage >= totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
          size={size}
          variant={variant}
          colorScheme={colorScheme}
          borderRadius="md"
        />
      </HStack>

      {showInfo && totalPages > 5 && (
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color={textColor}>
            Go to:
          </Text>
          <select
            value={currentPage}
            onChange={(e) => handlePageChange(Number(e.target.value))}
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              fontSize: '14px',
              background: 'transparent',
              color: textColor,
            }}
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNum) => (
                <option key={pageNum} value={pageNum}>
                  {pageNum}
                </option>
              )
            )}
          </select>
        </Flex>
      )}
    </Flex>
  );
};

export default Pagination;